import Vue from "vue";
import VueMeta from "vue-meta";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import mixins from "./mixins";
import { setupMomentSync } from "./utils/dateFormatter";
import { createProvider } from "./vue-apollo";
import vuetify from "./plugins/vuetify";
import i18n from "./i18n";
import AppLayout from "@/layouts/AppLayout";
import BaseAvatar from "@/components/Base/Avatar";
import BaseButton from "@/components/Base/Button";
import BaseCard from "@/components/Base/Card";
import BaseTitle from "@/components/Base/Title";
import * as Sentry from "@sentry/vue";
import "regenerator-runtime/runtime";
import GoogleAuth from "@/lib/gapi";
import Typeform from "@/plugins/typeform";
import { dropRouterNavigationNoise } from "@/utils/sentryFilters";
import { isEmbedPath } from "@/utils/embedPath";
import "@/styles/reset.scss";
import "@typeform/embed/build/css/widget.css";
import "vue-filter-table-component";
import VueTelInputVuetify from "vue-tel-input-vuetify/lib";

// Componentes globales
Vue.component("AppLayout", AppLayout);
Vue.component("BaseAvatar", BaseAvatar);
Vue.component("BaseButton", BaseButton);
Vue.component("BaseCard", BaseCard);
Vue.component("BaseTitle", BaseTitle);

Vue.use(VueTelInputVuetify, {
  vuetify,
});

// Plugins y librerías
Vue.use(VueMeta);
Vue.use(GoogleAuth, { client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID });
Vue.use(Typeform);

Vue.mixin(mixins);
// Setup moment.js sync with i18n locale changes
Vue.mixin(setupMomentSync(i18n));

Vue.config.productionTip = false;

// Silence logs in production (except errors) - simpler way
if (process.env.NODE_ENV === "production") {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.warn = noop;
}

// DL-452: Session Replay is not recorded inside Horizon's iframe. Recording a coachee's session
// on video while embedded in another app is not acceptable, and it would also duplicate weight.
// The gate is a conditional integration inside the single init, NOT a second Sentry.init: a second
// init replaces the hub's client and with it the beforeSend that filters navigation noise
// (DL-1529, see tests/unit/sentry-init.spec.js).
const sentryIntegrations = [Sentry.browserTracingIntegration({ router })];
if (!isEmbedPath()) {
  sentryIntegrations.push(Sentry.replayIntegration());
}

Sentry.init({
  Vue,
  dsn: "https://919844b36afc30a6493f3e9537ddecaf@o4506348802080768.ingest.sentry.io/4506418649825280",
  environment: process.env.NODE_ENV,
  integrations: sentryIntegrations,
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/staging\.yourney\.io/,
    /^https:\/\/staging-api\.yourney\.io/,
    /^https:\/\/testing\.yourney\.io/,
    /^https:\/\/app\.yourney\.io/,
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Drop vue-router navigation failures (redirect/abort/cancel/duplicate): expected SPA behaviour
  beforeSend: dropRouterNavigationNoise,
});

new Vue({
  router,
  store,
  apolloProvider: createProvider(),
  vuetify,
  i18n,
  render: (h) => h(App),
}).$mount("#app");

// Make store available globally for PostHog tracking
if (typeof window !== "undefined") {
  window.$store = store;
}
